/**
 * InstallPrompt — Blok 82 PWA install UI for "Mijn Powersure".
 *
 * Three platforms, three behaviours:
 *   1. Android / Chrome / Edge: capture `beforeinstallprompt`, show button.
 *   2. iOS Safari: no event → show static instructie ("Deelknop → Zet op beginscherm").
 *   3. Desktop: same beforeinstallprompt, but fallback text mentioning adresbalk-icoon.
 *
 * Non-intrusive:
 *   - Card inline op /app, niet modaal.
 *   - Dismiss persistent via localStorage key 'powersure_pwa_dismissed_at'.
 *   - 14-day cooldown na dismiss; auto-verdwijnt als al geinstalleerd.
 */
import { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';

const DISMISSED_KEY = 'powersure_pwa_dismissed_at';
const COOLDOWN_DAYS = 14;

function isStandalone() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(display-mode: standalone)').matches
      || window.navigator?.standalone === true;
}

function isIOS() {
  if (typeof navigator === 'undefined') return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
}

function isDismissedRecently() {
  try {
    const ts = localStorage.getItem(DISMISSED_KEY);
    if (!ts) return false;
    const ms = Date.now() - Number(ts);
    return ms < COOLDOWN_DAYS * 24 * 3600 * 1000;
  } catch { return false; }
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    if (isStandalone() || isDismissedRecently()) return;
    // Macro Funnel Completion Sprint (Feb 2026) — fire `pwa_install_prompt_seen`
    // max ONE keer per browser-tab-sessie. sessionStorage gate voorkomt event
    // spam wanneer Chrome de prompt herhaalt na navigatie.
    const SESSION_FIRE_KEY = 'pw_pwa_prompt_seen_fired_v1';
    const fireOncePerSession = (source) => {
      try {
        if (sessionStorage.getItem(SESSION_FIRE_KEY)) return;
        sessionStorage.setItem(SESSION_FIRE_KEY, String(Date.now()));
      } catch { /* sessionStorage blocked → fire anyway */ }
      try {
        import('@/utils/tracking').then(({ trackFunnelEvent }) =>
          trackFunnelEvent('pwa_install_prompt_seen',
                            { flow: 'customer_app',
                              meta: { source } }));
      } catch { /* ignore */ }
    };

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
      fireOncePerSession('beforeinstallprompt');
    };
    const onInstalled = () => {
      setInstalled(true);
      setShow(false);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    // iOS: geen beforeinstallprompt, toon meteen instructie (eenmalig)
    if (isIOS() && !isStandalone()) {
      setShow(true);
      fireOncePerSession('ios_instruction');
    }
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISSED_KEY, String(Date.now())); } catch { /* ignore */ }
    setShow(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === 'accepted') setInstalled(true);
    setDeferred(null);
    setShow(false);
  };

  if (installed || !show) return null;

  const ios = isIOS();
  return (
    <div
      className="rounded-xl border border-emerald-400/25 bg-emerald-400/[0.06] p-3 flex items-start gap-3"
      data-testid="pwa-install-banner"
    >
      <div className="mt-0.5 text-emerald-300"><Download className="w-4 h-4" /></div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-semibold text-white">Installeer Mijn Powersure</div>
        <p className="text-[11.5px] text-white/65 leading-snug mt-0.5">
          {ios
            ? 'Open het deelmenu in Safari en kies "Zet op beginscherm" voor snelle toegang.'
            : 'Zet Powersure op uw beginscherm voor snelle toegang. Werkt op telefoon, tablet en desktop.'}
        </p>
        {!ios && deferred && (
          <button
            onClick={install}
            className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 rounded-md text-[12px] font-semibold transition-colors"
            data-testid="pwa-install-btn"
          >
            <Download className="w-3.5 h-3.5" /> Installeren
          </button>
        )}
      </div>
      <button
        onClick={dismiss}
        className="text-white/40 hover:text-white/70 transition-colors"
        aria-label="Sluiten"
        data-testid="pwa-install-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
